import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "@environments/environment";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

export interface ParsedJobData {
  title?: string;
  company?: string;
  type?: string;
  work_type?: string;
  location?: string;
  salary_min: number | null;
  salary_max: number | null;
  short_description: string;
  about_company: string;
  responsibilities: string[];
  requirements: string[];
  benefits: string[];
}

export interface OpenAIResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

// Type alias for casting if needed frequently
type EnvironmentType = typeof environment;

/**
 * Base service for OpenAI API interactions.
 * This service provides core functionality for making API calls to OpenAI.
 */
@Injectable({
  providedIn: "root",
})
export class OpenAIBaseService {
  protected http = inject(HttpClient);
  // Always use the secure Supabase Edge Function proxy
  private apiUrl =
    "https://ykimklpimdbuwxceyngs.supabase.co/functions/v1/openai-proxy";

  constructor() {
    // No need to check for API key since we're using the secure proxy
  }

  /**
   * Get headers for OpenAI API requests
   */
  protected getOpenAIHeaders(): HttpHeaders {
    // Use Supabase anon key for Edge Function authentication
    return new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${environment.supabaseKey}`,
    });
  }

  /**
   * Make a request to the OpenAI chat completions API
   */
  protected async makeOpenAIChatRequest(
    prompt: string,
    systemPrompt: string,
    model: string = "gpt-4o-mini",
    temperature: number = 0.3,
    useJsonMode: boolean = false,
    maxTokens: number = 2000
  ): Promise<string> {
    const headers = this.getOpenAIHeaders();

    const requestBody: any = {
      model,
      messages: [
        {
          role: "system",
          content: systemPrompt,
        },
        {
          role: "user",
          content: prompt,
        },
      ],
      temperature,
      max_tokens: maxTokens,
    };

    // Enable JSON mode for guaranteed valid JSON responses
    if (useJsonMode) {
      requestBody.response_format = { type: "json_object" };
    }

    const response = await this.http
      .post<OpenAIResponse>(
        this.apiUrl,
        requestBody,
        { headers }
      )
      .toPromise();

    if (!response?.choices?.length) {
      throw new Error("No response from OpenAI API");
    }

    return this.processOpenAIResponse(response);
  }

  /**
   * Process the OpenAI API response and extract the content
   */
  protected processOpenAIResponse(response: OpenAIResponse): string {
    let content = response.choices[0].message.content;

    // Clean up markdown formatting if present
    if (content.includes("```")) {
      const codeBlockMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (codeBlockMatch && codeBlockMatch[1]) {
        content = codeBlockMatch[1].trim();
      } else {
        content = content
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();
      }
    }

    return content;
  }

  /**
   * Try to parse JSON from a string. Strips markdown code fences and, on
   * failure, tries to extract a single JSON object (first { to last }).
   */
  protected parseJsonResponse(content: string): any {
    let cleaned = content.trim();
    if (cleaned.startsWith("```json")) {
      cleaned = cleaned.slice(7).trim();
    }
    if (cleaned.startsWith("```")) {
      cleaned = cleaned.slice(3).trim();
    }
    if (cleaned.endsWith("```")) {
      cleaned = cleaned.slice(0, -3).trim();
    }
    try {
      return JSON.parse(cleaned);
    } catch {
      const first = cleaned.indexOf("{");
      const last = cleaned.lastIndexOf("}");
      if (first !== -1 && last > first) {
        try {
          return JSON.parse(cleaned.slice(first, last + 1));
        } catch {
          // fall through to throw
        }
      }
      throw new Error("Failed to parse OpenAI response as JSON");
    }
  }
}
